import { Dispatch, SetStateAction, useRef, useState, useEffect } from 'react';
import {
  alpha,
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Typography,
  useTheme,
  Button,
  Grid,
} from '@mui/material';
import Popper from '@mui/material/Popper';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TuneIcon from '@mui/icons-material/Tune';
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import {
  searchSuggestionsData,
  currentToastState,
  stocksState_SUBSCRIBE_TO_POLLING_RENDER,
} from 'states';
import {
  MoviefoneRiskTolerance,
  moviefoneTradeFiltersState,
} from 'states/moviefone';
import { StockDirection } from 'gen/stream-api/requests/types.gen';
import CanvasSymSelector from 'components/canvas/components/CanvasSymSelector';
import useEquities from 'hooks/equityhub/useEquities';
import { unionBy } from 'lodash';
import { HiroOverview } from 'types/hiro';
import { ExtendedStructureRequest } from 'types/moviefone';
import { SuggestionSearchData } from 'types/shared';

interface MoviefoneTradeCriteriaProps {
  tradeCriteria: ExtendedStructureRequest;
  setTradeCriteria: Dispatch<SetStateAction<ExtendedStructureRequest>>;
  sym: string;
  // Threaded through by the caller (standalone page vs. a future canvas widget) rather than
  // owned here, so the symbol persists via whichever mechanism that caller already uses.
  onSymChange: (sym: string) => void;

  highlightIcon: boolean;
}

const MoviefoneTradeCriteria = ({
  tradeCriteria,
  setTradeCriteria,
  sym,
  onSymChange,
  highlightIcon,
}: MoviefoneTradeCriteriaProps) => {
  const theme = useTheme();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [tradeFilters, setTradeFilters] = useRecoilState(
    moviefoneTradeFiltersState,
  );
  const symOptions = useRecoilValue(searchSuggestionsData);
  const timeFrameDays = tradeCriteria.view?.horizon_days ?? 0;
  const direction = tradeCriteria.view?.stock_direction ?? 'neutral';
  const min_pnl_ratio = tradeCriteria.bounds?.min_reward_to_risk ?? 0;
  const { riskTolerance, max_pnl_ratio } = tradeFilters;

  const { getSymbols } = useEquities();
  const setSuggestionsData = useSetRecoilState(searchSuggestionsData);
  const setToast = useSetRecoilState(currentToastState);
  const stocks = useRecoilValue(stocksState_SUBSCRIBE_TO_POLLING_RENDER);
  useEffect(() => {
    async function fetchEquities() {
      try {
        const symsObj: Record<string, string> = await getSymbols();
        const ehStockSuggestions: SuggestionSearchData[] = Object.entries(
          symsObj,
        ).map(([sym, name]: [string, string]) => ({
          symbol: sym,
          name: name,
        }));
        const hiroStockSuggestions: SuggestionSearchData[] = Array.from(
          stocks.values(),
        ).map((data: HiroOverview) => ({
          symbol: data.instrument,
          name: data.companyName,
        }));
        setSuggestionsData(
          unionBy(ehStockSuggestions, hiroStockSuggestions, 'symbol'),
        );
      } catch (err) {
        console.error(err);
        setToast({
          message:
            'Something went wrong while fetching equities data. Try again or contact us if the issue persists.',
          type: 'error',
        });
      }
    }
    void fetchEquities();
  }, [getSymbols, setSuggestionsData, setToast, stocks]);

  return (
    <>
      <IconButton
        data-ph-capture-attribute-element="moviefone-trade-criteria-open"
        ref={anchorRef}
        aria-label="Trade Criteria"
        onClick={() => setOpen((prev) => !prev)}
        size="small"
        sx={{
          color: theme.palette.text.secondary,
          '&:hover': {
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
          },
          flexShrink: 0,
          boxShadow: highlightIcon
            ? `0 0 8px 4px ${alpha(theme.palette.sgGreen, 1)}`
            : undefined,

          transition: 'box-shadow 1s',
        }}
      >
        <TuneIcon fontSize="small" />
      </IconButton>
      <Popper open={open} anchorEl={anchorRef.current} placement="top-end">
        <Box
          sx={{
            width: '480px',
            maxWidth: '90vw',
            mb: 1,
            border: `1px solid ${theme.palette.text.disabled}`,
            borderRadius: '8px',
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.palette.shadows.default,
            overflow: 'hidden',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ padding: '10px 20px 0' }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: theme.palette.text.primary }}
            >
              Trade Criteria
            </Typography>
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px',
              padding: '4px 20px 20px',
            }}
          >
            {/* Column 1 */}
            <Stack sx={{ gap: '16px' }}>
              <CanvasSymSelector
                sym={sym}
                options={symOptions}
                onSymChange={onSymChange}
                disablePortal
              />

              <FormControl size="small" fullWidth>
                <InputLabel id="trade-criteria-risk-label">
                  Trade Risk Score
                </InputLabel>
                <Select
                  labelId="trade-criteria-risk-label"
                  label="Trade Risk Score"
                  value={riskTolerance}
                  onChange={(e) =>
                    setTradeFilters((prev) => ({
                      ...prev,
                      riskTolerance: e.target.value as MoviefoneRiskTolerance,
                    }))
                  }
                  IconComponent={KeyboardArrowDownIcon}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.sgGreen,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.sgGreen,
                    },
                  }}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel id="trade-criteria-direction-label">
                  Direction
                </InputLabel>
                <Select
                  labelId="trade-criteria-direction-label"
                  label="Direction"
                  value={direction}
                  onChange={(e) =>
                    setTradeCriteria((prev) => ({
                      ...prev,
                      view: {
                        ...prev.view,
                        stock_direction: e.target.value as StockDirection,
                      },
                    }))
                  }
                  IconComponent={KeyboardArrowDownIcon}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.sgGreen,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.sgGreen,
                    },
                  }}
                >
                  <MenuItem value="bullish">Bullish</MenuItem>
                  <MenuItem value="bearish">Bearish</MenuItem>
                  <MenuItem value="neutral">Neutral</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {/* Column 2 */}
            <Stack sx={{ gap: '16px' }}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary, mb: 1 }}
                >
                  Time Frame ({timeFrameDays}{' '}
                  {timeFrameDays === 1 ? 'day' : 'days'})
                </Typography>
                <Slider
                  value={timeFrameDays}
                  onChange={(_, value) =>
                    setTradeCriteria((prev) => ({
                      ...prev,
                      view: { ...prev.view, horizon_days: value as number },
                    }))
                  }
                  min={0}
                  max={90}
                  step={1}
                  valueLabelDisplay="auto"
                  size="small"
                />
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary, mb: 1 }}
                >
                  Profit/Loss Ratio Range ({min_pnl_ratio} -{' '}
                  {Number.isFinite(max_pnl_ratio) ? max_pnl_ratio : '∞'})
                </Typography>
                <Slider
                  value={[
                    min_pnl_ratio,
                    Number.isFinite(max_pnl_ratio) ? max_pnl_ratio : 100,
                  ]}
                  onChange={(_, value) => {
                    const [min, max] = value as number[];
                    setTradeCriteria((prev) => ({
                      ...prev,
                      bounds: { ...prev.bounds, min_reward_to_risk: min },
                    }));
                    setTradeFilters((prev) => ({
                      ...prev,
                      max_pnl_ratio: max === 100 ? Infinity : max,
                    }));
                  }}
                  min={0}
                  max={100}
                  step={1}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => (value === 100 ? '∞' : value)}
                  size="small"
                />
              </Box>
            </Stack>
          </Box>
          <Grid item sx={{ width: '100%', marginTop: -8 }}>
            <Button
              variant="contained"
              size="small"
              sx={{
                float: 'right',
                margin: 4,
                width: '45px',
                fontFamily: 'Satoshi Complete',
                fontSize: 12,
                textTransform: 'capitalize',
              }}
              onClick={() => setOpen(false)}
            >
              Close
            </Button>
          </Grid>
        </Box>
      </Popper>
    </>
  );
};

export default MoviefoneTradeCriteria;
